import {
  BottomNav,
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarUser,
  Symbol,
  Toaster,
  Wordmark,
  cn,
  titleCase,
  useBreakpoint,
} from '@westhetics/ui'
import { LogOut, Menu } from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'
import { pendingStepCount, useOnboarding } from '@/features/onboarding/api'
import { isUsableClinic, useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  navAdminFor,
  navMobile,
  navOnboarding,
  navOperations,
  navWesthetics,
  type NavItem,
} from './nav'

/**
 * Casca do sistema: sidebar (200/76px) + conteúdo, ou gaveta + barra inferior abaixo de 900px.
 * A sidebar começa recolhida entre `desk` e `wide` (iPad paisagem, MacBook pequeno) e aberta em telas
 * largas; a escolha da pessoa é salva por dispositivo. Atalho de teclado: `[`.
 *
 * O menu encurta pelo perfil da sessão (RN-AUT-22): root vê o grupo Westhetics; sem clínica ativa,
 * só ele. Enquanto a clínica está em implantação, gestora e root veem o item "Implantação" com as
 * etapas que faltam (RN-ADM-80).
 */

const SIDEBAR_KEY = 'wx-sidebar'

function readSidebarPreference(): boolean | null {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_KEY)
    return raw === null ? null : raw === 'collapsed'
  } catch {
    return null
  }
}

export function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const session = useSession()
  const isWide = useBreakpoint('wide')
  const isDesk = useBreakpoint('desk')

  const [preference, setPreference] = useState<boolean | null>(readSidebarPreference)
  const collapsed = preference ?? !isWide
  // A gaveta do celular lembra em que endereço foi aberta: qualquer navegação (menu, rodapé ou um
  // link da tela) a fecha sem precisar de efeito.
  const [drawerOpenedAt, setDrawerOpenedAt] = useState<string | null>(null)
  const drawerOpen = drawerOpenedAt === location.pathname

  const toggle = useCallback(() => {
    setPreference((current) => {
      const next = !(current ?? !isWide)
      try {
        window.localStorage.setItem(SIDEBAR_KEY, next ? 'collapsed' : 'open')
      } catch {
        /* sem storage */
      }
      return next
    })
  }, [isWide])

  // Atalho "[" recolhe e abre a sidebar (fora de campos de texto).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null
      if (e.key !== '[' || target?.closest('input, textarea, select, [contenteditable]')) return
      toggle()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  const profile = session.profile
  const role = profile?.role ?? 'receptionist'
  const hasClinic = Boolean(profile?.clinicId)
  // O seletor só existe para quem tem mais de um lugar para estar: root, ou mais de uma clínica
  // utilizável (docs/areas/autenticacao.md, "Troca de clínica dentro da sessão").
  const canSwitchClinic = Boolean(
    profile && (profile.isRoot || profile.clinics.filter(isUsableClinic).length > 1),
  )
  const onboarding = useOnboarding({ enabled: session.canManageClinic })
  const pendingSteps = pendingStepCount(onboarding.data)

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`)
  const closeDrawer = () => setDrawerOpenedAt(null)

  const renderItem = (item: NavItem, badge?: number) => (
    <SidebarItem
      key={item.href}
      {...item}
      badge={badge || undefined}
      active={isActive(item.href)}
      LinkComponent={Link}
      onClick={closeDrawer}
    />
  )

  const home = session.isGlobalRoot ? routes.admin.clinics : routes.dashboard

  return (
    <div className="bg-pampas flex min-h-dvh">
      <Sidebar
        collapsed={isDesk && collapsed}
        onToggle={toggle}
        open={drawerOpen}
        onClose={closeDrawer}
        brand={
          <Link to={home} aria-label="Westhetics — início" className="text-ink block">
            <Wordmark className="h-[13px] w-auto" />
          </Link>
        }
        brandCollapsed={
          <Link to={home} aria-label="Westhetics — início" className="text-ink block">
            <Symbol className="h-[22px] w-auto" />
          </Link>
        }
        footer={
          <SidebarUser
            name={profile ? titleCase(profile.name) : ''}
            clinic={profile?.activeClinic?.name ?? (profile?.isRoot ? 'Westhetics' : '')}
            accountHref={routes.account}
            accountLabel="Sua conta: acessos, aplicativos conectados e senha"
            LinkComponent={Link}
            onSignOut={() => {
              void session.signOut().then(() => navigate(routes.login))
            }}
            onSwitch={
              canSwitchClinic
                ? () => {
                    closeDrawer()
                    navigate(routes.chooseClinic, { state: { switching: true } })
                  }
                : undefined
            }
          />
        }
      >
        {profile?.isRoot ? (
          <SidebarGroup label="Westhetics">
            {navWesthetics.map((item) => renderItem(item))}
            {hasClinic ? (
              <SidebarItem
                label="Sair da clínica"
                icon={<LogOut strokeWidth={1.4} />}
                onClick={() => {
                  closeDrawer()
                  void session.switchClinic(null).then(() => navigate(routes.admin.clinics))
                }}
              />
            ) : null}
          </SidebarGroup>
        ) : null}
        {hasClinic ? (
          <>
            <SidebarGroup label="Administração">
              {pendingSteps > 0 ? renderItem(navOnboarding, pendingSteps) : null}
              {navAdminFor(role).map((item) => renderItem(item))}
            </SidebarGroup>
            <SidebarGroup label="Operação">
              {navOperations.map((item) => renderItem(item))}
            </SidebarGroup>
          </>
        ) : null}
      </Sidebar>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Celular e iPad retrato: barra superior com ☰ e wordmark. */}
        <div className="h-header border-hairline bg-surface pt-safe desk:hidden flex items-center gap-4 border-b px-4">
          <button
            type="button"
            onClick={() => setDrawerOpenedAt(location.pathname)}
            aria-label="Abrir menu"
            className="size-touch text-ink -ml-2 flex cursor-pointer items-center justify-center"
          >
            <Menu strokeWidth={1.4} className="size-5" />
          </button>
          <Link to={home} aria-label="Westhetics — início" className="text-ink">
            <Wordmark className="h-[12px] w-auto" />
          </Link>
        </div>

        <main
          className={cn(
            'flex-1',
            hasClinic && 'max-desk:pb-[calc(var(--spacing-touch)+env(safe-area-inset-bottom,0px))]',
          )}
        >
          <Outlet />
        </main>
      </div>

      {hasClinic ? (
        <BottomNav
          LinkComponent={Link}
          className="pb-safe"
          items={navMobile.map((item) => ({ ...item, active: isActive(item.href) }))}
        />
      ) : null}
      <Toaster />
    </div>
  )
}

/**
 * Área de conteúdo padrão: respiro de 30px/28px no desktop, 20px/16px no celular. O contêiner tem
 * largura máxima (900px; 1120px com `wide`) e fica centrado no main, não encostado na sidebar (T36).
 */
export function Page({
  children,
  className,
  wide,
  full,
}: {
  children: ReactNode
  className?: string
  /** Listas e fichas com tabela: até 1120px. */
  wide?: boolean
  /** Sem limite de largura: o editor de documentos usa a tela inteira. */
  full?: boolean
}) {
  return (
    <div
      className={cn(
        'max-desk:px-4 max-desk:pt-5 max-desk:pb-8 px-7 pt-[30px] pb-[60px]',
        className,
      )}
    >
      <div className={cn(full ? '' : wide ? 'mx-auto max-w-[1120px]' : 'mx-auto max-w-[900px]')}>
        {children}
      </div>
    </div>
  )
}
